import React, { useEffect, useState } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography } from '@mui/material'
import moment from 'moment'
import { NumberField, Button, DateRangePicker } from '../../common/customComponents'
import { muiOptions, MuiProps, defaultStyles } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { api } from '../../../infrastructure/api'
import { MktSaleType, SimulatedMktSaleResult, StockFilters, StockProjection } from '../stockModels'
import { StockFiltersContainer } from '../filters/filtersStore'
import { SimulationBoardContainer } from './simulationBoardStore'
import { isAfterToday } from '../../../infrastructure/date'

type MktSalesMode = 'value' | 'percentage' | 'clear' | 'global'

type MktSalesFilters = Pick<StockFilters, 'productIds' | 'sites' | 'dutyStatuses' | 'companies'>

type SimulationMktSalesPopupProps = {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    projections: StockProjection[]
} & MuiProps

function _SimulationMktSalesPopup(props: SimulationMktSalesPopupProps) {
    let state = useSimulationMktSalesPopup(props)

    let modes: MktSalesMode[] = ['value', 'percentage', 'clear', 'global']
    let amountLabel = state.mode === 'percentage' ? t('stock.simulation.mktSalesPopup.percentageField')
        : state.mode === 'global' ? t('stock.simulation.mktSalesPopup.globalField')
            : t('stock.simulation.mktSalesPopup.valueField')
    let periodInvalid = state.isInvalidPeriod || state.isOutsideStockPeriod || state.isBeforeSimulationPeriod

    return (
        <Dialog open={props.isOpen} onClose={state.close} aria-labelledby='mkt-sales-simulation-dialog-title'>
            <DialogTitle id='mkt-sales-simulation-dialog-title'>{t('stock.simulation.mktSalesPopup.title')}</DialogTitle>
            <DialogContent className={props.classes.dialogContent}>
                <FormControl>
                    <FormLabel>{t('stock.simulation.mktSalesPopup.modeLabel')}</FormLabel>
                    <RadioGroup value={state.mode} onChange={(_, v) => state.setMode(v as MktSalesMode)}>
                        {modes.map(x =>
                            <FormControlLabel key={x} value={x} control={<Radio />}
                                label={t('stock.simulation.mktSalesPopup.modes.' + x)} />
                        )}
                    </RadioGroup>
                </FormControl>
                <DateRangePicker startDate={state.startDate} startLabel={t('stock.simulation.mktSalesPopup.startDate')}
                    endDate={state.endDate} endLabel={t('stock.simulation.mktSalesPopup.endDate')} autoReplace={true}
                    onPeriodChange={period => state.setPeriod(period.startDate, period.endDate)} disableNewStyle
                    startStatus={periodInvalid ? 'alert' : 'none'} endStatus={periodInvalid ? 'alert' : 'none'} />
                {state.mode !== 'clear' &&
                    <NumberField label={amountLabel} allowNegative={false} min={0} disableNewStyle
                        unit={state.mode === 'percentage' ? '%' : null} text={state.amount} onChange={state.setAmount} />}
                {state.isBeforeSimulationPeriod &&
                    <Typography color='error' variant='caption'>{t('stock.simulation.mktSalesPopup.startDateBeforeTomorrow')}</Typography>}
                {state.errorMessage &&
                    <Typography color='error' variant='caption'>{state.errorMessage}</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={state.close}
                    label={t('components.dialogClose')}
                    color='primary'
                    disabled={state.isApplying}
                    className={props.classes.cancelButton} />
                <Button onClick={state.apply}
                    label={t('components.dialogConfirm')}
                    color='primary'
                    disabled={!state.canApply}
                    className={props.classes.confirmButton} />
            </DialogActions>
        </Dialog>
    )
}

function useSimulationMktSalesPopup(props: SimulationMktSalesPopupProps) {
    let stockFiltersStore = StockFiltersContainer.useContainer()
    let simulationStore = SimulationBoardContainer.useContainer()

    let [mode, setMode] = useState<MktSalesMode>('value')
    let [amount, setAmount] = useState<number | null>(null)
    let [startDate, setStartDate] = useState<string | null>(null)
    let [endDate, setEndDate] = useState<string | null>(null)
    let [isApplying, setIsApplying] = useState<boolean>(false)
    let [errorMessage, setErrorMessage] = useState<string | null>(null)

    useEffect(() => {
        if (!props.isOpen) return
        setMode('value')
        setAmount(null)
        setStartDate(stockFiltersStore.filters.values.start)
        setEndDate(stockFiltersStore.filters.values.end)
        setErrorMessage(null)
    }, [props.isOpen])

    let hasPeriod = !!startDate && !!endDate
    let isInvalidPeriod = hasPeriod && moment(startDate).isAfter(moment(endDate), 'day')
    let isBeforeSimulationPeriod = !!startDate && !isAfterToday(startDate)
    let stockStart = stockFiltersStore.filters.values.start
    let stockEnd = stockFiltersStore.filters.values.end
    let isOutsideStockPeriod = hasPeriod && (
        (!!stockStart && moment.utc(startDate).isBefore(moment.utc(stockStart), 'day')) ||
        (!!stockEnd && moment.utc(endDate).isAfter(moment.utc(stockEnd), 'day')))
    let canApply = hasPeriod && !isInvalidPeriod && !isOutsideStockPeriod && !isBeforeSimulationPeriod
        && (mode === 'clear' || amount !== null) && !isApplying

    let setPeriod = (start: string | null, end: string | null) => {
        setStartDate(start)
        setEndDate(end)
    }

    let close = () => {
        if (!isApplying) props.setIsOpen(false)
    }

    let getPercentageEntries = (start: string, end: string, percentage: number) =>
        props.projections
            .filter(x => isInPeriod(x.date, start, end))
            .map(x => ({
                date: x.date,
                value: (x.volumeTypes.find(v => v.type === MktSaleType)?.volume ?? 0) * percentage / 100
            }))

    let apply = async () => {
        if (!canApply || !startDate || !endDate) return
        setIsApplying(true)
        setErrorMessage(null)
        try {
            if (mode === 'clear') {
                simulationStore.clearMktSalesSimulations(startDate, endDate)
            }
            else if (amount !== null) {
                if (mode === 'percentage')
                    simulationStore.applyMktSalesSimulations(getPercentageEntries(startDate, endDate, amount), startDate, endDate)
                else {
                    let query = buildMktSalesSimulationQuery(stockFiltersStore.filters.values, startDate, endDate, mode, amount)
                    let result = await api.get<SimulatedMktSaleResult>(query)
                    simulationStore.applyMktSalesSimulations(result.days, startDate, endDate)
                }
            }
            props.setIsOpen(false)
        }
        catch {
            setErrorMessage(t('stock.simulation.mktSalesPopup.error'))
        }
        finally {
            setIsApplying(false)
        }
    }

    return {
        mode, setMode, amount, setAmount, startDate, endDate, setPeriod,
        hasPeriod, isInvalidPeriod, isOutsideStockPeriod, isBeforeSimulationPeriod, canApply, isApplying, errorMessage,
        close, apply
    }
}

let isInPeriod = (date: string, start: string, end: string) =>
    moment(date).isBetween(moment(start), moment(end), 'day', '[]')

let buildMktSalesSimulationQuery = (filters: MktSalesFilters, start: string, end: string, mode: 'value' | 'global', amount: number) => {
    let query = ''
    filters.productIds?.forEach(x => query += `ProductIds=${x}&`)
    filters.sites?.forEach(x => query += `Site=${x}&`)
    filters.dutyStatuses?.forEach(x => query += `DutyStatus=${x}&`)
    filters.companies?.forEach(x => query += `Company=${x}&`)
    query += `Start=${encodeURIComponent(start)}&End=${encodeURIComponent(end)}&`
    query += `${mode === 'value' ? 'Value' : 'Global'}=${amount}`
    return `stock/movement/mktsale/simulation?${query}`
}

let styles = _ =>
    createStyles({
        dialogContent: {
            display: 'flex',
            flexDirection: 'column',
            gap: '1em',
            width: '30em'
        },
        confirmButton: { ...defaultStyles.dialogPrimaryButton },
        cancelButton: { ...defaultStyles.dialogCloseButton }
    })

export let SimulationMktSalesPopup = withStyles(styles, muiOptions)(_SimulationMktSalesPopup)
